import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useStudentDepletionDetail, type ClassTemplate } from "@/hooks/useStudentDepletionDetail";
import type { RegularStudent } from "@/hooks/useRegularStudents";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const WEEKDAY_NAMES = ["จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส.", "อา."];
const THAI_MONTHS = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];

interface Props {
  student: RegularStudent | null;
  onClose: () => void;
}

// Build map: weekday (0=Sun..6=Sat) → total hours for that day
function buildWeekdayMap(templates: ClassTemplate[]) {
  const map = new Map<number, number>();
  for (const t of templates) {
    if (!t.is_active || !t.start_time || !t.end_time) continue;
    const [sh, sm] = t.start_time.split(":").map(Number);
    const [eh, em] = t.end_time.split(":").map(Number);
    const dur = (eh * 60 + em - sh * 60 - sm) / 60;
    if (dur <= 0) continue;
    // weekday in DB: 0=Mon..6=Sun → JS Date.getDay(): 0=Sun,1=Mon..6=Sat
    const jsDay = t.weekday === 6 ? 0 : t.weekday + 1;
    map.set(jsDay, (map.get(jsDay) ?? 0) + dur);
  }
  return map;
}

interface DayInfo {
  date: Date;
  hours: number;
  remainingAfter: number;
  depleted: boolean;
  isFirstDepletion: boolean;
}

function simulateDepletion(currentHours: number, weekdayMap: Map<number, number>, monthsAhead: number): DayInfo[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setMonth(end.getMonth() + monthsAhead);

  let remaining = currentHours;
  let foundDepletion = false;
  const days: DayInfo[] = [];
  const d = new Date(today);

  // หา minimum class hours เพื่อเช็คว่าเหลือพอเรียนคาบถัดไปไหม
  const classHoursValues = Array.from(weekdayMap.values()).filter(h => h > 0);
  const minClassHours = classHoursValues.length > 0 ? Math.min(...classHoursValues) : 0;

  while (d <= end) {
    const hrs = weekdayMap.get(d.getDay()) ?? 0;
    // เช็คก่อนหัก: ถ้าเหลือไม่พอสำหรับคาบนี้
    let isFirstDep = !foundDepletion && hrs > 0 && remaining < hrs;
    if (isFirstDep) foundDepletion = true;
    // หักชั่วโมง
    if (!foundDepletion && hrs > 0) {
      remaining -= hrs;
      // เช็คหลังหัก: ถ้าเหลือไม่พอสำหรับคาบถัดไปที่เล็กที่สุด
      if (!foundDepletion && remaining < minClassHours) {
        isFirstDep = true;
        foundDepletion = true;
      }
    }
    days.push({
      date: new Date(d),
      hours: hrs,
      remainingAfter: remaining,
      depleted: foundDepletion && hrs > 0,
      isFirstDepletion: isFirstDep,
    });
    d.setDate(d.getDate() + 1);
  }
  return days;
}

export function StudentScheduleCalendarModal({ student, onClose }: Props) {
  const { classTemplates, isLoading } = useStudentDepletionDetail(student?.code ?? null);
  const [monthOffset, setMonthOffset] = useState(0);

  const weekdayMap = useMemo(() => buildWeekdayMap(classTemplates), [classTemplates]);

  const simulation = useMemo(() => {
    if (!student || student.hours == null) return [];
    return simulateDepletion(student.hours, weekdayMap, 4);
  }, [student, weekdayMap]);

  // Current display month
  const today = new Date();
  const displayMonth = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const monthLabel = `${THAI_MONTHS[displayMonth.getMonth()]} ${displayMonth.getFullYear() + 543}`;

  // Get days for display month
  const daysInMonth = new Date(displayMonth.getFullYear(), displayMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1).getDay();
  // Convert Sun=0 to Mon-based: Mon=0..Sun=6
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const dayMap = useMemo(() => {
    const m = new Map<string, DayInfo>();
    for (const d of simulation) {
      const key = `${d.date.getFullYear()}-${d.date.getMonth()}-${d.date.getDate()}`;
      m.set(key, d);
    }
    return m;
  }, [simulation]);

  const getDayInfo = (dayNum: number) => {
    const key = `${displayMonth.getFullYear()}-${displayMonth.getMonth()}-${dayNum}`;
    return dayMap.get(key);
  };

  // Find first depletion day
  const firstDepletionDay = simulation.find((d) => d.depleted);

  return (
    <Dialog open={!!student} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            ตารางเรียน: {student?.first_name} {student?.nickname ? `(${student.nickname})` : ""} — {student?.code}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            ชั่วโมงคงเหลือ: <span className="font-semibold">{student?.hours?.toFixed(1) ?? "-"}</span> ชม.
            {firstDepletionDay && (
              <span className="ml-2 text-destructive font-medium">
                คาดว่าจะหมดวันที่ {firstDepletionDay.date.getDate()} {THAI_MONTHS[firstDepletionDay.date.getMonth()]} {firstDepletionDay.date.getFullYear() + 543}
              </span>
            )}
          </p>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : classTemplates.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">
            ไม่พบตารางเรียนประจำของนักเรียนคนนี้
          </div>
        ) : (
          <div>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => setMonthOffset((v) => v - 1)} disabled={monthOffset <= 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-semibold">{monthLabel}</span>
              <Button variant="outline" size="sm" onClick={() => setMonthOffset((v) => v + 1)} disabled={monthOffset >= 3}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {WEEKDAY_NAMES.map((w) => (
                <div key={w} className="text-center text-xs font-medium text-muted-foreground py-1">
                  {w}
                </div>
              ))}
              {/* Empty cells for offset */}
              {Array.from({ length: startOffset }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const info = getDayInfo(dayNum);
                const hasClass = info && info.hours > 0;
                const isFirstDepletion = info?.isFirstDepletion;
                const isDepleted = info?.depleted;
                const isToday =
                  dayNum === today.getDate() &&
                  displayMonth.getMonth() === today.getMonth() &&
                  displayMonth.getFullYear() === today.getFullYear();

                return (
                  <div
                    key={dayNum}
                    className={`rounded-md border p-1.5 min-h-[60px] text-xs transition-colors
                      ${isFirstDepletion ? "bg-red-50 border-red-300 dark:bg-red-950 dark:border-red-800" : hasClass && !isDepleted ? "bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800" : "border-border"}
                      ${isToday ? "ring-2 ring-primary" : ""}
                    `}
                  >
                    <div className="font-medium">{dayNum}</div>
                    {isFirstDepletion && info && (
                      <>
                        <div className="text-[10px] text-red-600">{info.hours.toFixed(1)} ชม.</div>
                        <div className="text-[10px] text-red-600">เหลือ {info.remainingAfter.toFixed(1)}</div>
                        <div className="text-[10px] font-semibold text-red-600">ชม. หมด</div>
                      </>
                    )}
                    {hasClass && info && !isDepleted && !isFirstDepletion && (
                      <>
                        <div className="text-[10px] text-muted-foreground">{info.hours.toFixed(1)} ชม.</div>
                        {info.remainingAfter >= 0 && (
                          <div className={`text-[10px] font-semibold ${info.remainingAfter <= 8 ? "text-orange-600" : "text-foreground"}`}>
                            เหลือ {info.remainingAfter.toFixed(1)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-blue-50 border border-blue-200 dark:bg-blue-950 dark:border-blue-800" /> วันที่มีเรียน
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded ring-2 ring-primary" /> วันนี้
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-red-50 border border-red-300 dark:bg-red-950 dark:border-red-800" /> วันที่คาดว่าชั่วโมงจะหมด
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
